import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { Helmet } from "react-helmet";

import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Hidden,
  Typography,
} from "@mui/material";

import { containerStyles, headerStyles } from "pages/settings/utils/styles";
import { NotificationSettingItem } from "./components";
import { LOAD_NOTIFICATION_SETTINGS_QUERY } from "globals/graphql";
import { useOperator } from "globals/hooks";
import { NotificationSetting, TextRecipientEnum } from "types";
import { moovsBlue, moovsBlueSelected } from "design-system/colors";
import { FeedbackModal } from "design-system/components/layout/SideNav/components/HelpFAB/components";

const { None } = TextRecipientEnum;

const {
  SALES_AUTOMATION_ENROLLMENT,
  SALES_AUTOMATION_1ST_REMINDER,
  SALES_AUTOMATION_2ND_REMINDER,
  DRIVER_PENDING_JOB_ASSIGNMENT_REMINDER,
  DRIVER_TRIP_REMINDER_1_HOUR,
  DRIVER_TRIP_REMINDER_24_HOURS,
  CUSTOMER_TRIP_REMINDER_1_HOUR,
  CUSTOMER_TRIP_REMINDER_24_HOURS,
  DRIVER_ON_THE_WAY,
  DRIVER_ON_LOCATION,
  PASSENGER_ON_BOARD,
  TRIP_COMPLETED,
  SHUTTLE_IS_RUNNING_LATE,
  TRIP_CANCELLED_MESSAGE_FOR_DRIVER,
  FLIGHT_TIME_UPDATED_FOR_MEMBERS,
  FLIGHT_TIME_UPDATED_FOR_DRIVER,
} = {
  SALES_AUTOMATION_ENROLLMENT: "Sales Automation Enrollment",
  SALES_AUTOMATION_1ST_REMINDER: "Sales Automation 1st Reminder",
  SALES_AUTOMATION_2ND_REMINDER: "Sales Automation 2nd Reminder",
  DRIVER_PENDING_JOB_ASSIGNMENT_REMINDER:
    "Driver Pending Job Assignment Reminder",
  DRIVER_TRIP_REMINDER_1_HOUR: "Driver Trip Reminder - 1 Hour",
  DRIVER_TRIP_REMINDER_24_HOURS: "Driver Trip Reminder - 24 Hours",
  CUSTOMER_TRIP_REMINDER_1_HOUR: "Customer Trip Reminder - 1 Hour",
  CUSTOMER_TRIP_REMINDER_24_HOURS: "Customer Trip Reminder - 24 Hours",
  DRIVER_ON_THE_WAY: "Driver On the Way",
  DRIVER_ON_LOCATION: "Driver On-Location",
  PASSENGER_ON_BOARD: "Passenger on Board",
  TRIP_COMPLETED: "Trip Completed",
  SHUTTLE_IS_RUNNING_LATE: "Shuttle is Running Late",
  TRIP_CANCELLED_MESSAGE_FOR_DRIVER: "Trip Cancelled Message for Driver",
  FLIGHT_TIME_UPDATED_FOR_MEMBERS: "Flight time updated for members",
  FLIGHT_TIME_UPDATED_FOR_DRIVER: "Flight time updated for driver",
};

// custom notification order
const notificationOrder = {
  [SALES_AUTOMATION_ENROLLMENT]: 1,
  [SALES_AUTOMATION_1ST_REMINDER]: 2,
  [SALES_AUTOMATION_2ND_REMINDER]: 3,
  [CUSTOMER_TRIP_REMINDER_1_HOUR]: 4,
  [CUSTOMER_TRIP_REMINDER_24_HOURS]: 5,
  [DRIVER_ON_THE_WAY]: 6,
  [DRIVER_ON_LOCATION]: 7,
  [PASSENGER_ON_BOARD]: 8,
  [TRIP_COMPLETED]: 9,
  [SHUTTLE_IS_RUNNING_LATE]: 10,
};

const EXCLUDED_NOTIFICATIONS = [
  DRIVER_PENDING_JOB_ASSIGNMENT_REMINDER,
  DRIVER_TRIP_REMINDER_1_HOUR,
  DRIVER_TRIP_REMINDER_24_HOURS,
  TRIP_CANCELLED_MESSAGE_FOR_DRIVER,
  FLIGHT_TIME_UPDATED_FOR_MEMBERS,
  FLIGHT_TIME_UPDATED_FOR_DRIVER,
];

function NotificationsPage() {
  // hooks
  const {
    settings,
    driverReminderPendingJobsTextEnabled,
    driverReminderTripReminderTextsEnabled,
    driverStatusUpdateTextsEnabled,
  } = useOperator() || {};

  // state
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);

  const {
    tripReminderTextTo,
    pobStatusTextToBcEnabled,
    memberChangedStatusTextEnabled,
  } = settings || {};

  // queries
  const { data, loading } = useQuery<{
    loadNotificationSettings: NotificationSetting[];
  }>(LOAD_NOTIFICATION_SETTINGS_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  const result = data?.loadNotificationSettings;

  // memoize to exclude notifications and sort in specific order
  const notificationSettings = useMemo(
    () =>
      result
        ? [...result]
            .filter((notification) => {
              return !EXCLUDED_NOTIFICATIONS.includes(
                notification.templateName
              );
            })
            .sort((a, b) => {
              const orderA = notificationOrder[a.templateName] || 999;
              const orderB = notificationOrder[b.templateName] || 999;
              return orderA - orderB;
            })
        : [],
    [result]
  );

  // memoize settingIsDisabledMap
  const settingIsDisabledMap = useMemo(() => {
    const tripStatusUpdateDisabled =
      !driverStatusUpdateTextsEnabled && !memberChangedStatusTextEnabled;
    const tripReminderDisabled = tripReminderTextTo === None ? true : false;

    return {
      [DRIVER_ON_THE_WAY]: tripStatusUpdateDisabled,
      [DRIVER_ON_LOCATION]: tripStatusUpdateDisabled,
      [PASSENGER_ON_BOARD]:
        !pobStatusTextToBcEnabled || tripStatusUpdateDisabled,
      [TRIP_COMPLETED]: tripStatusUpdateDisabled,
      [CUSTOMER_TRIP_REMINDER_1_HOUR]: tripReminderDisabled,
      [CUSTOMER_TRIP_REMINDER_24_HOURS]: tripReminderDisabled,
      [DRIVER_TRIP_REMINDER_1_HOUR]: !driverReminderTripReminderTextsEnabled,
      [DRIVER_TRIP_REMINDER_24_HOURS]: !driverReminderTripReminderTextsEnabled,
      [DRIVER_PENDING_JOB_ASSIGNMENT_REMINDER]:
        !driverReminderPendingJobsTextEnabled,
    };
  }, [
    driverReminderPendingJobsTextEnabled,
    driverReminderTripReminderTextsEnabled,
    pobStatusTextToBcEnabled,
    tripReminderTextTo,
    driverStatusUpdateTextsEnabled,
    memberChangedStatusTextEnabled,
  ]);

  // event handlers
  const handleOpenFeedbackModal = () => {
    setFeedbackModalOpen(true);
  };

  const handleCloseFeedbackModal = () => {
    setFeedbackModalOpen(false);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        width="100%"
        height="100%"
      >
        <CircularProgress size={40} thickness={2} />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Notifications | Moovs</title>
      </Helmet>
      <Box sx={containerStyles}>
        <Box sx={headerStyles}>
          <Box display="flex" mt={2}>
            <Typography variant="h2">Notifications</Typography>
            <Chip
              label="BETA"
              size="small"
              sx={{
                py: 2,
                px: 1,
                fontSize: 14,
                ml: 2,
                bgcolor: moovsBlueSelected,
                color: moovsBlue,
                fontWeight: 600,
              }}
            />
          </Box>
          <Box mt={2} mb={1}>
            <Typography variant="body2">
              This feature is in beta and available to a limited group of
              operators. For now, existing settings still apply and this screen
              is for SMS customization only. Eventually, all notification
              controls will be moved here. Share feedback{" "}
              <Typography
                variant="body2"
                sx={{
                  display: "inline",
                  cursor: "pointer",
                  color: moovsBlue,
                  textDecoration: "underline",
                }}
                onClick={handleOpenFeedbackModal}
              >
                here
              </Typography>
              .
            </Typography>
          </Box>
        </Box>
        <Hidden mdDown>
          <Box px={3}>
            <Divider />
          </Box>
        </Hidden>
        <Box display="flex" flexDirection="column" gap={2} my={3}>
          {notificationSettings.map((notificationSetting) => {
            return (
              <NotificationSettingItem
                key={notificationSetting.id}
                notificationSetting={notificationSetting}
                isDisabled={
                  settingIsDisabledMap[notificationSetting.templateName]
                }
              />
            );
          })}
        </Box>
      </Box>
      <FeedbackModal
        open={feedbackModalOpen}
        onClose={handleCloseFeedbackModal}
      />
    </>
  );
}

export default NotificationsPage;
